import { Handle, Position } from "@xyflow/react";
import { Key } from "lucide-react";
import type { Permission, RiskSeverity } from "@/lib/arch-parser";
import { cn } from "@/lib/utils";

export interface PermissionNodeData {
  permission: Permission;
  faded?: boolean;
  highlighted?: boolean;
  // v0.4 grouped L3-of-external layout. When `band` is set, the permission
  // renders as a full-width header bar above its endpoint grid instead of a
  // fixed-width card. `endpointCount` is shown in the band ("8 endpoints" /
  // "No endpoints documented").
  band?: boolean;
  endpointCount?: number;
  [key: string]: unknown;
}

// Severity dots map to the status tokens so they swap with the theme — no
// hardcoded color scales, no dark: overrides (spec §1.8).
const SEVERITY_DOT: Record<RiskSeverity, string> = {
  info: "bg-ink-subtle",
  warning: "bg-warning",
  critical: "bg-danger",
};

const CONSENT_CHIP: Record<string, string> = {
  delegated: "bg-info-soft text-info-ink border-info/30",
  application: "bg-warning-soft text-warning-ink border-warning/30",
  other: "bg-surface-tint text-ink-subtle border-border",
};

export function PermissionNode({
  data,
  selected,
}: {
  data: PermissionNodeData;
  selected?: boolean;
}) {
  const p = data.permission;
  const consentClass = CONSENT_CHIP[p.consent_surface] ?? CONSENT_CHIP.other;

  if (data.band) {
    const count = data.endpointCount ?? 0;
    const countLabel =
      count > 0 ? `${count} endpoint${count === 1 ? "" : "s"}` : "No endpoints documented";
    return (
      <div
        className={cn(
          "relative rounded-md border-[1.5px] px-4 py-2.5 shadow-sm transition-all w-full flex items-center gap-3",
          "bg-[var(--node-other-fill)] border-[var(--node-other-border)] text-[var(--node-other-ink)]",
          "opacity-95",
          selected &&
            "ring-2 ring-offset-2 ring-offset-background ring-[color:var(--node-selection-ring)] shadow-md opacity-100 z-10",
          data.faded && "opacity-30",
          data.highlighted && "ring-2 ring-teal-400/70 opacity-100",
        )}
      >
        <Handle type="target" position={Position.Top} id="top" className="!bg-foreground/30" />
        <Key className="h-3.5 w-3.5 shrink-0" />
        <span
          className="font-mono font-semibold leading-tight tracking-tight shrink-0"
          style={{ fontSize: "calc(13px * var(--archviz-label-scale, 1))" }}
        >
          {p.scope}
        </span>
        <span
          className={cn(
            "text-[9px] font-medium px-1.5 py-0.5 rounded border uppercase shrink-0",
            consentClass,
          )}
        >
          {p.consent_surface}
        </span>
        <span
          className={cn("h-2 w-2 rounded-full shrink-0", SEVERITY_DOT[p.severity])}
          title={`severity: ${p.severity}`}
        />
        <span className="text-[10px] text-muted-foreground shrink-0">{countLabel}</span>
        {p.what_it_enables && (
          <span className="text-[10px] text-muted-foreground/80 truncate ml-1 min-w-0">
            {p.what_it_enables}
          </span>
        )}
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom-src"
          className="!bg-foreground/30"
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative rounded-md border-[1.5px] px-3 py-2 shadow-sm transition-all min-w-[220px] max-w-[280px]",
        "bg-[var(--node-other-fill)] border-[var(--node-other-border)] text-[var(--node-other-ink)]",
        "opacity-95",
        selected &&
          "ring-2 ring-offset-2 ring-offset-background ring-[color:var(--node-selection-ring)] shadow-md opacity-100 z-10",
        data.faded && "opacity-30",
        data.highlighted && "ring-2 ring-teal-400/70 opacity-100",
      )}
    >
      <Handle type="target" position={Position.Top} id="top" className="!bg-foreground/30" />
      <Handle type="target" position={Position.Bottom} id="bottom" className="!bg-foreground/30" />
      <div className="flex items-start gap-1.5">
        <Key className="h-3 w-3 shrink-0 mt-0.5" />
        <div
          className="font-mono font-semibold leading-tight tracking-tight break-all"
          style={{ fontSize: "calc(12px * var(--archviz-label-scale, 1))" }}
        >
          {p.scope}
        </div>
        <span
          className={cn("h-2 w-2 rounded-full shrink-0 ml-auto mt-1", SEVERITY_DOT[p.severity])}
          title={`severity: ${p.severity}`}
        />
      </div>
      <div className="mt-1.5 flex items-center gap-1">
        <span
          className={cn(
            "text-[9px] font-medium px-1.5 py-0.5 rounded border uppercase",
            consentClass,
          )}
        >
          {p.consent_surface}
        </span>
      </div>
      {p.what_it_enables && (
        <div className="text-[10px] text-muted-foreground leading-snug mt-1.5">
          {p.what_it_enables}
        </div>
      )}
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-src"
        className="!bg-foreground/30"
      />
    </div>
  );
}
